import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/cn'

/**
 * Decorative hero illustration for the /signup left panel, transcribed (not redrawn) from the
 * Keycloak sign-in theme's own control-plane diagram — docs/reference/keycloak-login-theme/entite.css
 * (`#kc-header::after`'s inline SVG, viewBox 0 0 500 392) — per PRD docs/prd/signup-match-signin-visual.md.
 *
 * AC3b colour-discipline comment block (the single enumeration point a reviewer reads instead of
 * auditing the SVG body):
 *   - `var(--color-primary)` — the header-bar gradient's darker stop. `entite.css`'s own token comment
 *     states its hex form (#2d4498) IS this app's --primary; referencing the token here (rather than
 *     repeating that hex) is possible specifically because this is inline SVG in the real DOM, unlike
 *     the reference's CSS background-image data-URI, which cannot resolve `var()` at all.
 *   - `#3f5ec4` — the gradient's lighter stop. An illustration-only highlight tint with no equivalent
 *     value anywhere in src/index.css's token set (verified — it is not a hidden duplicate of any
 *     token, so 3b's "must not be a hardcoded equivalent" rule does not apply to it).
 *   - `#8fa3e8`, `#cfd8fa` — connector lines, hub/sync dots, and diagram label text, all INSIDE the
 *     SVG. Illustration-only tints ported verbatim from the reference, which documents
 *     (entite.css:38-47) that these were tried as CSS tokens and reverted: nothing ever consumed
 *     them via `var()`, and an SVG's own fill attributes can't read a custom property from outside
 *     the document anyway once rasterised — the same reasoning applies here even though this SVG
 *     lives in the DOM, since these tints don't correspond to any app brand colour to begin with.
 *     (An MR review correctly caught that the stat row's "source of truth" label previously reused
 *     `#8fa3e8` as a literal `text-[#8fa3e8]` className on a plain DOM `<Text>` element — AC3b's
 *     carve-out is for SVG fill attributes specifically, which cannot resolve `var()`; it was never
 *     meant to cover ordinary DOM text, which the token system already handles. Fixed: that label
 *     now uses the `inverse-subtle` Text variant like its siblings, and this literal exists only
 *     inside the SVG below.)
 *   - Flag colours (India/UAE/UK/USA/Singapore) — real national flag palettes, unrelated to the app's
 *     brand palette by definition.
 *   - White / white-with-opacity (`#fff`, `rgba(255,255,255,…)`) used for chip fills, borders, and the
 *     divider line inside the diagram — decorative illustration chrome, distinct from the
 *     `text-text-inverse`-token treatment used for the page's actual wordmark/headline copy (T02); nothing
 *     here is real, readable UI text.
 *   - Nothing above is added to index.css, Tailwind config, or any shared stylesheet — all of it is
 *     scoped to this one file's SVG markup.
 */

const JURISDICTIONS: {
  code: string
  // PLACEHOLDER — NOT REAL DATA. See the block comment above STAT_ROW below for the full caveat.
  count: number
  flag: React.ReactNode
}[] = [
  {
    code: 'IN',
    count: 31,
    flag: (
      <>
        <rect x="-9" y="-6" width="18" height="4" fill="#FF9933" />
        <rect x="-9" y="-2" width="18" height="4" fill="#FFFFFF" />
        <rect x="-9" y="2" width="18" height="4" fill="#138808" />
        <circle cx="0" cy="0" r="1.5" fill="none" stroke="#0A2D8F" strokeWidth="0.7" />
      </>
    ),
  },
  {
    code: 'UAE',
    count: 18,
    flag: (
      <>
        <rect x="-9" y="-6" width="18" height="4" fill="#00732F" />
        <rect x="-9" y="-2" width="18" height="4" fill="#FFFFFF" />
        <rect x="-9" y="2" width="18" height="4" fill="#000000" />
        <rect x="-9" y="-6" width="5" height="12" fill="#FF0000" />
      </>
    ),
  },
  {
    code: 'UK',
    count: 24,
    flag: (
      <>
        <rect x="-9" y="-6" width="18" height="12" fill="#012169" />
        <path d="M-9 -6 L9 6 M9 -6 L-9 6" stroke="#FFFFFF" strokeWidth="2.8" />
        <path d="M-9 -6 L9 6 M9 -6 L-9 6" stroke="#C8102E" strokeWidth="1.2" />
        <path d="M0 -6 V6 M-9 0 H9" stroke="#FFFFFF" strokeWidth="4" />
        <path d="M0 -6 V6 M-9 0 H9" stroke="#C8102E" strokeWidth="2" />
      </>
    ),
  },
  {
    code: 'USA',
    count: 12,
    flag: (
      <>
        <rect x="-9" y="-6" width="18" height="12" fill="#FFFFFF" />
        {[-6, -2.57, 0.86, 4.29].map((y) => (
          <rect key={y} x="-9" y={y} width="18" height="1.71" fill="#B31942" />
        ))}
        <rect x="-9" y="-6" width="7.5" height="6" fill="#0A3161" />
      </>
    ),
  },
  {
    code: 'SG',
    count: 9,
    flag: (
      <>
        <rect x="-9" y="-6" width="18" height="6" fill="#ED2939" />
        <rect x="-9" y="0" width="18" height="6" fill="#FFFFFF" />
        <circle cx="-4.6" cy="-3" r="2.3" fill="#FFFFFF" />
        <circle cx="-3" cy="-3" r="2.3" fill="#ED2939" />
      </>
    ),
  },
]

const CHIP_X = [52, 151, 250, 349, 448]

const DOT_GRID_GROUPS = [28, 127, 226, 325, 424]

export interface ControlPlaneArtworkProps {
  className?: string
}

/**
 * The control-plane diagram + stat row, together, as ONE decorative unit — both are hidden from
 * assistive tech (PRD AC7: this hero panel's figures are known-invented and must never be announced
 * as page content). No focusable descendants anywhere below this wrapper.
 */
export function ControlPlaneArtwork({ className }: ControlPlaneArtworkProps) {
  return (
    <div aria-hidden="true" className={cn('select-none', className)}>
      <svg viewBox="0 0 500 392" fill="none" className="w-full h-auto">
        <defs>
          <linearGradient id="signup-control-plane-header" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3f5ec4" stopOpacity="0.58" />
            <stop offset="1" stopColor="var(--color-primary)" stopOpacity="0.26" />
          </linearGradient>
        </defs>

        {/* Header bar */}
        <rect
          x="12" y="12" width="476" height="82" rx="15"
          fill="url(#signup-control-plane-header)" stroke="#8fa3e8" strokeOpacity="0.66"
        />
        <path d="M27 13 h446" stroke="#fff" strokeOpacity="0.26" />
        <text x="34" y="44" fill="#cfd8fa" fontSize="10.5" letterSpacing="1.8">CONTROL PLANE</text>
        <text x="34" y="74" fill="#fff" fontSize="16" fontWeight="600">One register, every jurisdiction</text>
        <g transform="translate(392,36)">
          <circle cx="0" cy="4" r="3.6" fill="#8fa3e8" />
          <circle cx="0" cy="4" r="8" stroke="#8fa3e8" strokeOpacity="0.35" fill="none" />
          <text x="14" y="8" fill="#cfd8fa" fillOpacity="0.9" fontSize="10.5" letterSpacing="0.6">SYNCED</text>
        </g>

        {/* Hub + connectors */}
        <g stroke="#8fa3e8" strokeOpacity="0.34" strokeWidth="1.4">
          {CHIP_X.map((x) => (
            <path key={x} d={`M250 94 V142 H${x} V196`} />
          ))}
        </g>
        <circle cx="250" cy="94" r="3.6" fill="#8fa3e8" />

        {/* Jurisdiction chips */}
        {JURISDICTIONS.map((j, i) => {
          const x = CHIP_X[i]
          return (
            <g key={j.code}>
              <rect
                x={x - 40} y="196" width="80" height="62" rx="11"
                fill="#fff" fillOpacity="0.075" stroke="#fff" strokeOpacity="0.22"
              />
              <path d={`M${x - 29} 197 h58`} stroke="#fff" strokeOpacity="0.16" />
              <g transform={`translate(${x},209)`}>{j.flag}</g>
              <rect
                x={x - 9} y="203" width="18" height="12"
                fill="none" stroke="#fff" strokeOpacity="0.45" strokeWidth="0.6"
              />
              <text x={x} y="236" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="600">
                {j.code}
              </text>
              {/* PLACEHOLDER — NOT REAL DATA. See STAT_ROW comment below for the full caveat. */}
              <text x={x} y="251" textAnchor="middle" fill="#fff" fillOpacity="0.52" fontSize="10">
                {j.count} entities
              </text>
            </g>
          )
        })}

        {/* Dot-grid decoration */}
        <g fill="#8fa3e8">
          {DOT_GRID_GROUPS.map((gx) =>
            [278, 293].flatMap((gy) =>
              [0, 16, 32, 48]
                .filter((dx) => !(gy === 293 && dx === 48))
                .map((dx) => (
                  <circle
                    key={`${gx}-${gy}-${dx}`}
                    cx={gx + dx}
                    cy={gy}
                    r="3.1"
                    fillOpacity={dx === 0 || dx === 48 ? 0.24 : 0.46}
                  />
                )),
            ),
          )}
        </g>

        <line x1="12" y1="344" x2="488" y2="344" stroke="#fff" strokeOpacity="0.13" />
      </svg>

      {/*
       * PLACEHOLDER — NOT REAL DATA. 132 entities / 14 jurisdictions and every per-jurisdiction count
       * above (IN 31, UAE 18, UK 24, USA 12, SG 9) are INVENTED FIGURES, ported verbatim from the
       * Keycloak sign-in theme's own hero per docs/prd/signup-match-signin-visual.md §5-§6. They are a
       * recorded PRODUCTION BLOCKER against keycloak-custom-login-theme T05/T06 — see
       * entity-backend/docs/tasks/keycloak-custom-login-theme/T09-ink-panel-control-plane.md:70-72,325
       * — and MUST NOT reach production without sign-off. Shipping this component extends that same
       * blocker to a SECOND surface; it does not resolve it.
       */}
      {/* Text pieces go through <Text> (CLAUDE.md: all text via the shared component), sized via
          `.signup-hero-stat-figure`/`-stat-label` (index.css — see the comment there for why a
          bare Tailwind arbitrary size class silently does nothing here) and coloured via the
          `inverse`/`inverse-subtle` Text variants, never a literal className colour — the exact
          discipline that would have caught the `text-inverse` (vs. the actually-generated
          `text-text-inverse`) naming bug here too, and that an MR review correctly caught missing
          on "source of truth" below (it used to hardcode `text-[#8fa3e8]`, an accent tint that is
          legitimate INSIDE the SVG per AC3b but was never actually justified for a plain DOM
          <Text> element — AC3b's carve-out is for SVG fill attributes specifically, because those
          cannot resolve `var()`; this is ordinary DOM text, where the token system works fine, so
          it now matches its sibling labels exactly). `inverse-subtle` (60%, measures 5.94:1
          against --ink) replaces the ad-hoc `opacity-55` (~5.2:1) these two labels carried — a
          consolidation onto a named, reused, slightly-higher-margin tier, not a new number. */}
      <div className="mt-2 flex items-baseline gap-4">
        <span className="flex items-baseline gap-1.5">
          <Text as="span" color="inverse" className="signup-hero-stat-figure">132</Text>
          <Text as="span" color="inverse-subtle" className="signup-hero-stat-label">entities</Text>
        </span>
        <span className="flex items-baseline gap-1.5">
          <Text as="span" color="inverse" className="signup-hero-stat-figure">14</Text>
          <Text as="span" color="inverse-subtle" className="signup-hero-stat-label">jurisdictions</Text>
        </span>
        <span className="flex items-baseline gap-1.5">
          <Text as="span" color="inverse" className="signup-hero-stat-figure">1</Text>
          <Text as="span" color="inverse-subtle" className="signup-hero-stat-label">source of truth</Text>
        </span>
      </div>
    </div>
  )
}
